import type { MyPlanDto, PlanCatalogDto, PlanDto, PlanUsageDto } from '@dbrain/shared';
import { useQuery } from '@tanstack/react-query';
import { useCapabilities, useMeta } from '../experience.js';
import { api } from './client.js';
import { useMe } from './hooks.js';

/**
 * Phase 16a (ADR-0023, 06 §28.4): the person's plan where the edition has plans (bireysel), and what follows from it
 * on the screens. An edition without plans gets null and the screens stay as they were.
 */
export const planKeys = { usage: ['me', 'plan'] as const, catalog: ['plans'] as const };

export function usePlan(): MyPlanDto | null {
  const capabilities = useCapabilities();
  const me = useMe();
  return capabilities.plans ? (me.data?.plan ?? null) : null;
}

/** Today's use, the documents kept and the own connections (GET /api/me/plan) */
export function usePlanUsage(options: { enabled?: boolean; refetchMs?: number } = {}) {
  const capabilities = useCapabilities();
  return useQuery({
    queryKey: planKeys.usage,
    queryFn: () => api<PlanUsageDto>('GET', '/api/me/plan'),
    enabled: capabilities.plans && (options.enabled ?? true),
    refetchInterval: options.refetchMs ?? 60_000,
  });
}

/** The plans on sale, for the comparison and the price on "Pro'ya geç · 500 TL/ay" (Phase 16c: visitors too) */
export function usePlanCatalog() {
  const capabilities = useCapabilities();
  return useQuery({ queryKey: planKeys.catalog, queryFn: () => api<PlanCatalogDto>('GET', '/api/plans'), enabled: capabilities.plans, staleTime: 5 * 60_000 });
}

export function useProPlan(): PlanDto | null {
  return usePlanCatalog().data?.find((plan) => plan.id === 'pro' && plan.onSale) ?? null;
}

/** D-266: the largest file a person may attach or upload now: their plan's, or the installation's */
export function useUploadLimitMb(): number {
  const plan = usePlan();
  const meta = useMeta();
  return plan?.maxFileMb ?? meta.data?.uploadMaxMb ?? 100;
}

/**
 * An organization's admin (its owner or an admin) where the edition has organizations. A person's own account owns
 * its space, but that gives it nothing to manage (ADR-0023 §2).
 */
export function useOrganizationAdmin(): boolean {
  const capabilities = useCapabilities();
  const role = useMe().data?.tenant.role;
  return capabilities.organization && (role === 'owner' || role === 'admin');
}
